/**
 * Move dialog (#123): a folder picker for "Move…" on a document. Browses with
 * the caller's listFolders (files.list, folders only), shows breadcrumbs and
 * resolves the chosen folder id (null = root) or undefined when cancelled.
 * Built like ConfirmDialog: Bootstrap modal, shared queue, textContent only.
 */

import { enqueueDialog } from './dialog-queue';
import { queuedModal } from './queued-modal';
import type { FolderListing, FolderRef } from '../core/files-service';

export interface MoveDialogOptions {
  title: string;
  rootLabel: string;
  moveHereLabel: string;
  cancelLabel: string;
  loadingText: string;
  noFoldersText: string;
  currentFolderId: string | null;
  listFolders: (folderId: string | null) => Promise<FolderListing>;
  errorText: (error: unknown) => string;
}

let instanceCounter = 0;

export function moveDialog(
  options: MoveDialogOptions
): Promise<string | null | undefined> {
  return new Promise((resolve) => {
    enqueueDialog((done) => {
      try {
        openDialog(options, resolve, done);
      } catch {
        resolve(undefined);
        done();
      }
    });
  });
}

function openDialog(
  options: MoveDialogOptions,
  resolve: (value: string | null | undefined) => void,
  done: () => void
): void {
  const id = `move-dialog-${++instanceCounter}`;

  const modal = document.createElement('div');
  modal.className = 'modal fade';
  modal.id = id;
  modal.tabIndex = -1;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${id}-title`);

  const content = document.createElement('div');
  content.className = 'modal-content';
  const header = document.createElement('div');
  header.className = 'modal-header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'modal-title fs-5';
  titleEl.id = `${id}-title`;
  titleEl.textContent = options.title;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn-close';
  close.setAttribute('aria-label', options.cancelLabel);
  header.append(titleEl, close);

  const body = document.createElement('div');
  body.className = 'modal-body';
  const crumbs = document.createElement('nav');
  crumbs.dataset.moveCrumbs = '';
  crumbs.className = 'move-dialog__crumbs';
  const list = document.createElement('div');
  list.className = 'list-group move-dialog__folders';
  list.dataset.moveFolders = '';
  const status = document.createElement('p');
  status.className = 'text-muted mb-0';
  status.dataset.moveStatus = '';
  status.setAttribute('role', 'status');
  body.append(crumbs, list, status);

  const footer = document.createElement('div');
  footer.className = 'modal-footer';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn btn-outline-secondary';
  cancel.dataset.moveAction = 'cancel';
  cancel.textContent = options.cancelLabel;
  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'btn btn-primary';
  confirm.dataset.moveAction = 'confirm';
  confirm.textContent = options.moveHereLabel;
  confirm.disabled = true;
  footer.append(cancel, confirm);

  content.append(header, body, footer);
  const dialog = document.createElement('div');
  dialog.className = 'modal-dialog modal-dialog-centered';
  dialog.appendChild(content);
  modal.appendChild(dialog);
  document.body.appendChild(modal);

  let browsing: string | null = null;
  let loadSeq = 0;
  let settled = false;
  const settle = (value: string | null | undefined): void => {
    if (settled) return;
    settled = true;
    resolve(value);
  };
  const { hide, show } = queuedModal(modal, {
    done,
    onClosed: () => settle(undefined),
  });

  const crumbButton = (crumb: { id: string | null; name: string }) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn-link p-0 me-2';
    b.dataset.moveCrumb = crumb.id ?? '';
    b.textContent = crumb.name;
    b.addEventListener('click', () => void browse(crumb.id));
    return b;
  };

  async function browse(folderId: string | null): Promise<void> {
    const seq = ++loadSeq;
    browsing = folderId;
    confirm.disabled = true;
    list.replaceChildren();
    status.textContent = options.loadingText;
    try {
      const listing = await options.listFolders(folderId);
      if (seq !== loadSeq) return;
      const path: { id: string | null; name: string }[] = [
        { id: null, name: options.rootLabel },
        ...listing.path.map((p: FolderRef) => ({ id: p.id, name: p.name })),
      ];
      crumbs.replaceChildren(...path.map(crumbButton));
      const folders = listing.items.filter((i) => i.kind === 'folder');
      list.replaceChildren(
        ...folders.map((f) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'list-group-item list-group-item-action';
          b.dataset.moveFolder = f.id;
          b.textContent = f.name;
          b.addEventListener('click', () => void browse(f.id));
          return b;
        })
      );
      status.textContent = folders.length === 0 ? options.noFoldersText : '';
      confirm.disabled = browsing === options.currentFolderId;
    } catch (error) {
      if (seq !== loadSeq) return;
      status.textContent = options.errorText(error);
    }
  }

  confirm.addEventListener('click', () => {
    settle(browsing);
    hide();
  });
  cancel.addEventListener('click', () => {
    settle(undefined);
    hide();
  });
  close.addEventListener('click', () => {
    settle(undefined);
    hide();
  });
  modal.addEventListener('hide.bs.modal', () => settle(undefined));

  if (show()) void browse(null);
}
